Token:
  parameters:
    - $ref: "../parameters.yaml#/QueryLanguage"

  post:
    operationId: postToken
    tags:
      - Auth
    summary: アクセストークンの発行
    description: |
      ### アカウント情報に基づきアクセストークンを発行します。

      ほとんどのAPI呼び出しには、アプリケーションのユーザーが自分のデータにアクセスできるよう認証が必要になります。
      認証手続きで「access_token」を発行し、これを利用して各APIを使用できます。

      DirectCloudは、アプリケーションがユーザーデータにアクセスできるよう認証する際に、ログインして作成されたセッションまたはトークンを使用します。

      > 💡 &nbsp; **Note**  <br><br>
      新規発行されアクセストークンはセキュリティのため60日間のみ有効です。有効期限切れのアクセストークンは破棄され、新規に発行されます。アクセストークンは接続環境毎に発行されますので、例えば、chromeで発行されたアクセストークンはIEでは使用できません。

      >**service, service_key** <br>
      管理ページ「共有」⇒「カスタム設定」メニューの「DirectCloud API」にて、「使用する」を選択することでAPIキー(Service、Service Key)を取得できます。

    requestBody:
      $ref: "../openapi/requests.yaml#/TokenRequest"

    responses:
      "200":
        description: トークン発行成功
        content:
          application/json:
            schema:
              $ref: "../openapi/responses.yaml#/Token"
      "default":
        description: トークン発行失敗
        content:
          application/json:
            examples:
              Invalid Input:
                $ref: "../openapi/response.error.yaml#/InputValueIsInvalid"
              No Account:
                $ref: "../openapi/response.error.yaml#/NoAccount"
              Do Not Have Permission:
                $ref: "../openapi/response.error.yaml#/DoNotHavePermission"

AccessToken:
  parameters:
    - $ref: "../parameters.yaml#/QueryLanguage"

  post:
    operationId: postToken
    tags:
      - Auth
    summary: アクセスキーによるアクセストークンの発行
    description: |
      ### アクセスキーを利用して、アクセストークンを発行します。

      管理ページで発行したアクセスキーをパラメータに入力してHTTPリクエストを送信することで、会社ID・ユーザーID・パスワードを入力することなくアクセストークンを発行することができます。<br>

      > 💡 &nbsp; **Note**  <br>
      >**access_key** <br>
      管理ページでアクセスキーを発行する手順については、以下の記事を参照してください。<br>
      <br>
      DirectCloudのAPIを利用するためのキーを取得する方法<br>
      [https://help.directcloud.jp/s/article/operation-of-administrator-function11164](https://help.directcloud.jp/s/article/operation-of-administrator-function11164)

    requestBody:
      $ref: "../openapi/requests.yaml#/AccessTokenRequest"

    responses:
      "200":
        description: トークン発行成功
        content:
          application/json:
            schema:
              $ref: "../openapi/responses.yaml#/Token"
      "default":
        description: トークン発行失敗
        content:
          application/json:
            examples:
              Invalid Input:
                $ref: "../openapi/response.error.yaml#/InputValueIsInvalid"
              No Account:
                $ref: "../openapi/response.error.yaml#/NoAccount"
              Do Not Have Permission:
                $ref: "../openapi/response.error.yaml#/DoNotHavePermission"

ManagerToken:
  parameters:
    - $ref: "../parameters.yaml#/QueryLanguage"

  post:
    operationId: postManagerToken
    tags:
      - Auth
    summary: 管理者用アクセストークンの発行
    description: |
      ### アカウント情報に基づき管理者用アクセストークンを発行します。

      DirectCloud APIを使用するには、ユーザーの認証が必要です。ユーザー認証を行い、`access_token`を発行することで、各APIの利用が可能になります。
      管理者機能の場合、一般ユーザーのトークンでは使用が不可能で、管理者として登録されたユーザーのトークンのみ使用可能です。
      
      > 💡 &nbsp; **Note**  <br><br>
      新規発行されたアクセストークンはセキュリティのため60日間のみ有効です。有効期限切れのトークンは破棄され、新規に発行されます。 アクセストークンは接続環境毎に発行されますので、例えば、chromeで発行されたアクセストークンはIEでは使用できません。


      >**service, service_key**
      管理ページ「共有」⇒「カスタム設定」メニューの「DirectCloud API」にて、「使用する」を選択することでAPIキー(Service、Service Key)を取得できます。

    requestBody:
      $ref: "../openapi/requests.yaml#/ManagerTokenRequest"
    
    responses:
      "200":
        description: 管理者トークン発行成功
        content:
          application/json:
            schema:
              $ref: "../openapi/responses.yaml#/Token"
      "default":
        description: 管理者トークン発行失敗
        content:
          application/json:
            examples:
              Invalid Input:
                $ref: "../openapi/response.error.yaml#/InputValueIsInvalid"
              No Account:
                $ref: "../openapi/response.error.yaml#/NoAccount"
              Do Not Have Permission:
                $ref: "../openapi/response.error.yaml#/DoNotHavePermission"

TokenExpire:
  parameters:
    - $ref: "../parameters.yaml#/QueryLanguage"

  post:
    operationId: postTokenExpire
    tags:
      - Auth
    summary: アクセストークンの破棄
    description: |
      ### アクセストークンを破棄します。
      ユーザーのログアウトまたは接続終了時に `access_token`を破棄します。

    requestBody:
      $ref: "../openapi/requests.yaml#/TokenExpireRequest"

    responses:
      "200":
        description: トークン破棄成功
        content:
          application/json:
            schema:
              $ref: "../openapi/responses.yaml#/Success"
      "default":
        description: 管理者トークン発行失敗
        content:
          application/json:
            examples:
              ㅤ:
                $ref: "../responses/response.error.yaml#/null"

ManagerTokenExpire:
  parameters:
    - $ref: "../parameters.yaml#/QueryLanguage"

  post:
    operationId: postManagerTokenExpire
    tags:
      - Auth
    summary: 管理者用アクセストークンの破棄
    description: |
      ### 管理者用アクセストークンを破棄します。
      使用者のログアウトまたは接続終了時に `access_token`を破棄します。

    requestBody:
      $ref: "../openapi/requests.yaml#/TokenExpireRequest"

    responses:
      "200":
        description: 管理者トークン破棄成功
        content:
          application/json:
            schema:
              $ref: "../openapi/responses.yaml#/Success"
      "default":
        description: 管理者トークン破棄失敗
        content:
          application/json:
            examples:
              ㅤ:
                $ref: "../responses/response.error.yaml#/null"
